import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@vinoga/db";
import {
  Plus,
  Pencil,
  Trash2,
  Package,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Calendar,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Search,
  X,
  Check,
  ChevronsUpDown,
  ExternalLink,
} from "lucide-react";
import { ADMIN_TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { mediaSrc } from "@/lib/media";
import { storefrontProductUrl } from "@/lib/storefront";

type ProductRow = {
  id: string;
  sku: string;
  slug: string;
  translations: any;
  active: boolean;
  abv: string | null;
  volume_ml: number | null;
  vintage: number | null;
  created_at: string;
};

type CategoryRow = { id: string; parent_id: string | null; slug: string; translations: any };

const PAGE_SIZE_OPTIONS = [50, 100, 250, 500] as const;

export function Products() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = useT();

  const [products, setProducts] = useState<ProductRow[]>([]);
  const [productCategories, setProductCategories] = useState<Record<string, string[]>>({});
  const [basePrices, setBasePrices] = useState<Record<string, string>>({});
  const [productStock, setProductStock] = useState<Record<string, number>>({});
  const [productImages, setProductImagesMap] = useState<Record<string, string>>({});
  const [basePriceListId, setBasePriceListId] = useState<string | null>(null);
  // Mirror of the above that fetchProducts reads. State would be captured
  // stale by an in-flight fetch created before the price list resolved.
  const basePriceListIdRef = useRef<string | null>(null);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);

  // List state lives in the URL (owner 2026-10-09): search → open a product →
  // browser "back" used to land on the default newest-first view. Seeding the
  // state from the query string (and mirroring changes back, replace-style)
  // makes back restore the exact search/filters/page the admin left.
  const [urlParams, setUrlParams] = useSearchParams();
  const [search, setSearch] = useState(urlParams.get("q") ?? "");

  const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3004";

  const [page, setPage] = useState(() => {
    const n = parseInt(urlParams.get("lapa") ?? "0", 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    const n = parseInt(urlParams.get("skaits") ?? "50", 10);
    return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : 50;
  });
  const [totalCount, setTotalCount] = useState(0);

  const [sortColumn, setSortColumn] = useState<string>(
    urlParams.get("sort") ?? "created_at",
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    urlParams.get("dir") === "asc" ? "asc" : "desc",
  );
  const [filterCategory, setFilterCategory] = useState<string>(
    urlParams.get("kat") ?? "",
  );
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>(
    urlParams.get("statuss") ?? "",
  );

  // Mirror list state → URL. replace:true so typing in the search box never
  // piles up history entries; the product-row navigation is the only push.
  useEffect(() => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    if (page > 0) p.set("lapa", String(page));
    if (pageSize !== 50) p.set("skaits", String(pageSize));
    if (sortColumn !== "created_at") p.set("sort", sortColumn);
    if (sortDirection !== "desc") p.set("dir", sortDirection);
    if (filterCategory) p.set("kat", filterCategory);
    if (filterStatus) p.set("statuss", filterStatus);
    setUrlParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, pageSize, sortColumn, sortDirection, filterCategory, filterStatus]);

  useEffect(() => {
    (async () => {
      const [c, pl] = await Promise.all([
        supabase
          .from("categories")
          .select("id, parent_id, slug, translations")
          .order("position"),
        // Fetch all price lists and pick "base" here rather than filtering with
        // .single(): the price column rendered "—" for every row because this
        // lookup could come back empty, which left basePriceListId null and
        // skipped the price query entirely.
        supabase.from("price_lists").select("id, code"),
      ]);
      setCategories(c.data ?? []);
      const base = (pl.data ?? []).find((r: any) => r.code === "base");
      basePriceListIdRef.current = base?.id ?? null;
      setBasePriceListId(base?.id ?? null);
    })();
  }, []);

  const categoryTree = useMemo(() => {
    const result: Array<{ cat: CategoryRow; depth: number; path: string }> = [];
    const nameOf = (c: CategoryRow) =>
      (c.translations as any)?.lv?.name ??
      (c.translations as any)?.en?.name ??
      c.slug;
    const walk = (parentId: string | null, depth: number, prefix: string) => {
      categories
        .filter((c) => c.parent_id === parentId)
        .forEach((c) => {
          const path = prefix ? `${prefix} → ${nameOf(c)}` : nameOf(c);
          result.push({ cat: c, depth, path });
          walk(c.id, depth + 1, path);
        });
    };
    walk(null, 0, "");
    return result;
  }, [categories]);

  const fetchProducts = async () => {
    setLoading(true);
    const from = page * pageSize;
    const to = from + pageSize - 1;

    // When a search term is set, route through the smart-search endpoint —
    // mirrors the storefront pipeline (accent-aware, multi-token AND, typo
    // tolerance, plus exact-match boost on SKU + barcode). The endpoint
    // returns an ordered ID list; we load full rows and preserve that order.
    let searchIds: string[] | null = null;
    const s = search.trim();
    if (s.length >= 2) {
      try {
        const token = localStorage.getItem(ADMIN_TOKEN_KEY);
        const res = await fetch(
          `${API_BASE}/api/v1/admin/products/search?q=${encodeURIComponent(s)}&limit=500`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (res.ok) {
          const json = (await res.json()) as { ids: string[] };
          searchIds = json.ids ?? [];
        }
      } catch {
        // fall through to no-search query
      }
    }

    // Stock sort: total stock is summed across locations, which the row
    // proxy can't order by — the API returns the whole catalogue's ids in
    // stock order and we paginate over that, so the order is global rather
    // than per page. Search keeps its relevance order instead.
    let stockIds: string[] | null = null;
    if (searchIds === null && sortColumn === "stock") {
      try {
        const token = localStorage.getItem(ADMIN_TOKEN_KEY);
        const res = await fetch(
          `${API_BASE}/api/v1/admin/products/stock-order?dir=${sortDirection}&status=${filterStatus}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (res.ok) {
          const json = (await res.json()) as { ids: string[] };
          stockIds = json.ids ?? [];
        }
      } catch {
        // fall through to the default order
      }
    }

    let query = supabase.from("products").select("*", { count: "exact" });

    if (searchIds !== null) {
      if (searchIds.length === 0) {
        setProducts([]);
        setTotalCount(0);
        setBasePrices({});
        setProductStock({});
        setProductCategories({});
        setProductImagesMap({});
        setLoading(false);
        return;
      }
      query = query.in("id", searchIds);
    }

    if (filterStatus === "active") query = query.eq("active", true);
    else if (filterStatus === "inactive") query = query.eq("active", false);

    const dbSortable = ["sku", "slug", "created_at", "active"];
    // When searching, preserve relevance order from the search endpoint;
    // skip the column sort so the top hit stays at the top.
    if (stockIds !== null) {
      const slice = stockIds.slice(from, to + 1);
      if (slice.length === 0) {
        setProducts([]);
        setTotalCount(stockIds.length);
        setLoading(false);
        return;
      }
      query = query.in("id", slice);
    } else if (searchIds === null) {
      if (dbSortable.includes(sortColumn)) {
        query = query.order(sortColumn, { ascending: sortDirection === "asc" });
      } else {
        query = query.order("created_at", { ascending: false });
      }
      query = query.range(from, to);
    }

    const { data, count, error } = await query;
    if (error) {
      toast({
        title: t("products.errorLoading", "Error loading products"),
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    let rows = (data ?? []) as ProductRow[];

    // Preserve the order the search / stock-order endpoint dictated.
    const orderIds = searchIds ?? stockIds;
    if (orderIds !== null) {
      const rank = new Map(orderIds.map((id, i) => [id, i]));
      rows = [...rows].sort(
        (a, b) => (rank.get(a.id) ?? 1e9) - (rank.get(b.id) ?? 1e9),
      );
    }

    if (filterCategory) {
      const descendantIds = (function collect(parentId: string): string[] {
        const children = categories.filter((c) => c.parent_id === parentId);
        return [parentId, ...children.flatMap((c) => collect(c.id))];
      })(filterCategory);

      const visibleIds = rows.map((p) => p.id);
      if (visibleIds.length) {
        const { data: pcRows } = await supabase
          .from("product_categories")
          .select("product_id, category_id")
          .in("product_id", visibleIds)
          .in("category_id", descendantIds);
        const matching = new Set((pcRows ?? []).map((r: any) => r.product_id));
        rows = rows.filter((p) => matching.has(p.id));
      }
    }

    setProducts(rows);
    // A stock-ordered page only loads its own slice, so the query count is the
    // page size — the catalogue total comes from the ordered id list.
    setTotalCount(stockIds !== null ? stockIds.length : (count ?? 0));

    const ids = rows.map((p) => p.id);
    if (ids.length) {
      const [priceRes, catRes, imgRes, stockRes] = await Promise.all([
        // Fetch every list's price and pick "base" below, rather than
        // filtering by basePriceListId here: that made the column depend on
        // the price-list lookup having resolved first, so any fetch that ran
        // before it silently produced no prices at all.
        supabase
          .from("product_prices")
          .select("product_id, price_list_id, amount")
          .in("product_id", ids),
        supabase
          .from("product_categories")
          .select("product_id, category_id")
          .in("product_id", ids),
        supabase
          .from("product_images")
          .select("product_id, url, position")
          .in("product_id", ids)
          .order("position", { ascending: true }),
        // Total across all locations (3 shops + warehouse) — the stock figure
        // staff care about on the list; the editor shows the per-shop split.
        supabase
          .from("inventory_stock")
          .select("product_id, qty")
          .in("product_id", ids),
      ]);
      // Read the id from the ref, not from state: this function may be running
      // from a closure created before the price list resolved.
      const baseId = basePriceListIdRef.current;
      const pmap: Record<string, string> = {};
      (priceRes.data ?? []).forEach((r: any) => {
        // Without a resolved base id we can't tell the lists apart, so show
        // nothing rather than risk displaying a special (B2B) price as the
        // shop price. In practice the id is always set by this point.
        if (!baseId || r.price_list_id !== baseId) return;
        pmap[r.product_id] = r.amount;
      });
      setBasePrices(pmap);
      const smap: Record<string, number> = {};
      (stockRes.data ?? []).forEach((r: any) => {
        smap[r.product_id] = (smap[r.product_id] ?? 0) + Number(r.qty ?? 0);
      });
      setProductStock(smap);
      const cmap: Record<string, string[]> = {};
      (catRes.data ?? []).forEach((r: any) => {
        cmap[r.product_id] = [...(cmap[r.product_id] ?? []), r.category_id];
      });
      setProductCategories(cmap);
      // First image per product (orderBy position ASC).
      const imap: Record<string, string> = {};
      for (const r of (imgRes.data ?? []) as Array<{ product_id: string; url: string }>) {
        if (!imap[r.product_id]) imap[r.product_id] = r.url;
      }
      setProductImagesMap(imap);
    } else {
      setBasePrices({});
      setProductStock({});
      setProductCategories({});
      setProductImagesMap({});
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortColumn, sortDirection, filterCategory, filterStatus, basePriceListId]);

  // Search debounce. Skipped on the very first render: on mount this timer
  // raced the effect above and landed LAST, holding a closure from before the
  // base price-list id had resolved — so it re-fetched with no price list and
  // overwrote the prices that had just loaded, blanking the whole column.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const t = setTimeout(() => {
      setPage(0);
      fetchProducts();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((p) => (p === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column)
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/30" />;
    return sortDirection === "asc" ? (
      <ChevronUp className="ml-1 h-3.5 w-3.5 shrink-0" />
    ) : (
      <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0" />
    );
  };

  // One-click activate/deactivate from the list. Optimistic so the switch
  // responds immediately; reverted with a toast if the write fails.
  const toggleActive = async (p: ProductRow) => {
    const next = !p.active;
    setProducts((prev) =>
      prev.map((row) => (row.id === p.id ? { ...row, active: next } : row)),
    );
    const { error } = await supabase
      .from("products")
      .update({ active: next })
      .eq("id", p.id);
    if (error) {
      setProducts((prev) =>
        prev.map((row) => (row.id === p.id ? { ...row, active: p.active } : row)),
      );
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    // The status filter would otherwise keep showing a row that no longer
    // matches it.
    if (filterStatus) fetchProducts();
  };

  const deleteProduct = async (id: string) => {
    if (!confirm(t("products.confirmDelete", "Delete this product?"))) return;
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.deleteFailed", "Delete failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    fetchProducts();
  };

  const filterCategoryLabel = filterCategory
    ? categoryTree.find(({ cat }) => cat.id === filterCategory)?.path ??
      t("products.allCategories", "All categories")
    : t("products.allCategories", "All categories");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-xl font-bold text-foreground">
            {t("products.title", "Products")}
            {totalCount > 0 && (
              <span className="ml-2 font-body text-sm font-normal text-muted-foreground">
                ({totalCount.toLocaleString()})
              </span>
            )}
          </h1>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={t("products.searchPlaceholder", "Search by SKU or slug...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-[260px] pl-8 pr-8 font-body text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={categoryOpen}
                className={cn(
                  "h-8 w-[220px] justify-between font-body text-sm font-normal",
                  filterCategory && "border-primary"
                )}
              >
                {filterCategoryLabel}
                <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder={t("products.searchCategories", "Search categories...")}
                />
                <CommandList>
                  <CommandEmpty>
                    {t("products.noCategoryFound", "No category found.")}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setFilterCategory("");
                        setCategoryOpen(false);
                        setPage(0);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          !filterCategory ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {t("products.allCategories", "All categories")}
                    </CommandItem>
                    {categoryTree.map(({ cat, depth, path }) => (
                      <CommandItem
                        key={cat.id}
                        value={`${path} ${cat.slug}`}
                        onSelect={() => {
                          setFilterCategory(cat.id);
                          setCategoryOpen(false);
                          setPage(0);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            filterCategory === cat.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span style={{ paddingLeft: `${depth * 12}px` }}>
                          {path}
                          <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                            /{cat.slug}
                          </span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Select
            value={filterStatus}
            onValueChange={(val) => {
              setFilterStatus(val === "__all" ? "" : val);
              setPage(0);
            }}
          >
            <SelectTrigger
              className={cn(
                "h-8 w-[130px] font-body text-sm",
                filterStatus && "border-primary"
              )}
            >
              <SelectValue placeholder={t("products.allStatuses", "All statuses")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("products.allStatuses", "All statuses")}</SelectItem>
              <SelectItem value="active">{t("products.statusActive", "Active")}</SelectItem>
              <SelectItem value="inactive">
                {t("products.statusInactive", "Inactive")}
              </SelectItem>
            </SelectContent>
          </Select>
          {(filterCategory || filterStatus) && (
            <button
              onClick={() => {
                setFilterCategory("");
                setFilterStatus("");
                setPage(0);
              }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <X size={12} /> {t("common.clearFilters", "Clear filters")}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => navigate("/products/new")}>
            <Plus size={16} /> {t("products.addProduct", "Add Product")}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-4 font-body text-sm text-muted-foreground">
          {t("common.loading", "Loading...")}
        </div>
      ) : products.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">
          {t("products.empty", "No products found.")}
        </p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-12 text-center font-bold">
                    {t("products.colImage", "Image")}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("sku")}
                  >
                    <div className="flex items-center">
                      {t("products.colSku", "SKU")}
                      <SortIcon column="sku" />
                    </div>
                  </TableHead>
                  <TableHead className="font-bold">
                    {t("products.colNameLv", "Name (LV)")}
                  </TableHead>
                  <TableHead className="font-bold">
                    {t("products.colCategory", "Category")}
                  </TableHead>
                  <TableHead className="text-right font-bold">
                    {t("products.colPrice", "Price")}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("stock")}
                  >
                    <div className="flex items-center justify-end">
                      {t("products.colStock", "Atlikums")}
                      <SortIcon column="stock" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center justify-center">
                      {t("products.colCreated", "Created")}
                      <SortIcon column="created_at" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center font-bold transition-colors hover:bg-muted/50"
                    onClick={() => handleSort("active")}
                  >
                    <div className="flex items-center justify-center">
                      {t("products.colStatus", "Status")}
                      <SortIcon column="active" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-bold">
                    {t("products.colActions", "Actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => {
                  const catIds = productCategories[p.id] ?? [];
                  const firstCat = catIds.length
                    ? categories.find((c) => c.id === catIds[0])
                    : null;
                  const catName = firstCat
                    ? (firstCat.translations as any)?.lv?.name ?? firstCat.slug
                    : "—";
                  return (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/products/${p.id}`)}
                    >
                      <TableCell className="py-1.5 text-center">
                        {(() => {
                          const img = mediaSrc(productImages[p.id] ?? null);
                          return img ? (
                            <div className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/30">
                              <img
                                src={img}
                                alt={(p.translations as any)?.lv?.name ?? p.slug}
                                className="h-full w-full object-contain p-0.5"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-muted/30">
                              <Package size={14} className="text-muted-foreground/40" />
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="py-2.5 font-mono text-xs">{p.sku}</TableCell>
                      <TableCell className="py-2.5">
                        <span className="font-medium text-foreground">
                          {(p.translations as any)?.lv?.name ?? p.slug}
                        </span>
                      </TableCell>
                      <TableCell className="py-2.5 text-muted-foreground">{catName}</TableCell>
                      <TableCell className="whitespace-nowrap py-2.5 text-right text-muted-foreground">
                        {basePrices[p.id]
                          ? `€${parseFloat(basePrices[p.id]!).toFixed(2)}`
                          : "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-2.5 text-right">
                        {(() => {
                          const qty = productStock[p.id];
                          if (qty === undefined) {
                            return <span className="text-muted-foreground">—</span>;
                          }
                          return (
                            <span
                              className={cn(
                                "font-body text-sm tabular-nums",
                                qty <= 0 ? "text-destructive" : "text-foreground",
                              )}
                            >
                              {qty}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="py-2.5 text-center text-muted-foreground">
                        <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                          <Calendar size={12} className="text-muted-foreground/50" />
                          <span className="font-body text-xs">
                            {new Date(p.created_at).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 text-center">
                        {/* Row click opens the editor, so the switch stops
                            propagation — otherwise toggling would navigate. */}
                        <div
                          className="flex justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Switch
                            checked={p.active}
                            onCheckedChange={() => toggleActive(p)}
                            aria-label={
                              p.active
                                ? t("products.statusActive", "Active")
                                : t("products.statusInactive", "Inactive")
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 text-right">
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => navigate(`/products/${p.id}`)}
                          >
                            <Pencil size={14} className="text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 hover:text-destructive"
                            onClick={() => deleteProduct(p.id)}
                          >
                            <Trash2 size={14} className="text-muted-foreground" />
                          </Button>
                          <Button
                            asChild
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            title={
                              p.active
                                ? t("products.openInStore", "Atvērt internetveikalā")
                                : t(
                                    "products.openInStoreInactive",
                                    "Produkts nav aktīvs — internetveikalā tas vēl nav redzams",
                                  )
                            }
                          >
                            <a
                              href={storefrontProductUrl(p.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink
                                size={14}
                                className={
                                  p.active
                                    ? "text-muted-foreground"
                                    : "text-muted-foreground/40"
                                }
                              />
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-3">
              <span className="font-body text-xs text-muted-foreground">
                {t("common.showing", "Showing")} {page * pageSize + 1}–
                {Math.min((page + 1) * pageSize, totalCount)} {t("common.of", "of")}{" "}
                {totalCount.toLocaleString()}
              </span>
              <Select
                value={String(pageSize)}
                onValueChange={(val) => {
                  setPageSize(Number(val));
                  setPage(0);
                }}
              >
                <SelectTrigger className="h-7 w-[100px] font-body text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size} / {t("common.page", "page")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page === 0}
                  onClick={() => setPage(0)}
                >
                  «
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={14} />
                </Button>
                <span className="px-3 font-body text-xs text-foreground">
                  {t("common.page", "Page")} {page + 1} {t("common.of", "of")} {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRightIcon size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(totalPages - 1)}
                >
                  »
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
